import React from "react";
import { motion } from "framer-motion";
import { Sparkles, HeartHandshake, Baby } from "lucide-react";
import { REALITY_CHECK, SOFT_SIDE, MOM_MODE } from "../data/about";
import { useSiteTexts } from "../lib/siteTexts";

const cardMotion = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function About() {
  const texts = useSiteTexts();
  const realityBody = texts.reality_body || REALITY_CHECK.body;
  const softBody = texts.soft_body || SOFT_SIDE.body;
  const momBody = texts.mom_body || MOM_MODE.body;
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.35em] text-[#7B9ACC]">
              Chapter one
            </p>
            <h2 className="mt-3 font-display font-bold text-4xl sm:text-5xl text-[#0B192C] tracking-tight">
              About the woman <br className="hidden sm:block" /> of the hour.
            </h2>
          </div>
          <p className="max-w-md font-body text-[#334E68] text-base leading-relaxed">
            Warning: contains gentle affection, deep pride, and one mild
            confession about coffee crimes. Ekdum honestly written.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          {/* About Onnii */}
          <motion.article
            {...cardMotion}
            data-testid="about-reality-check"
            className="md:col-span-8 relative rounded-3xl bg-white p-8 md:p-10 card-shadow overflow-hidden border border-[#EAF0F6]"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 rounded-full bg-[#EAF0F6] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#7B9ACC]" />
              </span>
              <p className="font-body text-xs uppercase tracking-[0.3em] text-[#7B9ACC]">
                {REALITY_CHECK.subtitle}
              </p>
            </div>
            <h3 className="font-display font-bold text-3xl md:text-4xl text-[#0B192C] tracking-tight">
              {REALITY_CHECK.title}
            </h3>
            {realityBody.split("\n\n").map((para, i) => (
              <p key={i} className="mt-5 font-body text-[#334E68] leading-relaxed text-base md:text-lg">
                {para}
              </p>
            ))}
            <div className="mt-8 flex flex-wrap gap-2">
              {REALITY_CHECK.tags.map((t) => (
                <span key={t} className="text-xs px-3 py-1.5 rounded-full bg-[#F0F5FA] text-[#334E68] font-body">
                  {t}
                </span>
              ))}
            </div>
          </motion.article>

          {/* Soft Side */}
          <motion.article
            {...cardMotion}
            data-testid="about-soft-side"
            className="md:col-span-4 rounded-3xl bg-[#E6F0F9] p-8 md:p-9 card-shadow border border-[#D6E4F0]"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                <HeartHandshake className="w-5 h-5 text-[#7B9ACC]" />
              </span>
              <p className="font-body text-xs uppercase tracking-[0.3em] text-[#7B9ACC]">
                {SOFT_SIDE.subtitle}
              </p>
            </div>
            <h3 className="font-display font-bold text-3xl text-[#0B192C] tracking-tight">
              {SOFT_SIDE.title}
            </h3>
            <p className="mt-5 font-body text-[#334E68] leading-relaxed">
              {softBody}
            </p>
            <p className="mt-6 font-hand text-[#7B9ACC] text-2xl leading-tight">
              she's the reason the family group chat still works.
            </p>
          </motion.article>

          {/* Mom Mode */}
          <motion.article
            {...cardMotion}
            data-testid="about-mom-mode"
            className="md:col-span-12 rounded-3xl bg-[#0B192C] text-[#F7F9FC] p-8 md:p-10 relative overflow-hidden"
          >
            <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#7B9ACC]/25 blur-3xl" />
            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7">
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Baby className="w-5 h-5 text-[#A7C7E7]" />
                  </span>
                  <p className="font-body text-xs uppercase tracking-[0.3em] text-[#A7C7E7]">
                    {MOM_MODE.subtitle}
                  </p>
                </div>
                <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                  {MOM_MODE.title}
                </h3>
                <p className="mt-5 font-body text-[#D6E4F0] leading-relaxed text-base md:text-lg max-w-2xl">
                  {momBody}
                </p>
              </div>
              <div className="md:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white/5 p-6">
                  <p className="font-hand text-[#A7C7E7] text-3xl leading-tight">
                    "Via, put it back."
                  </p>
                  <p className="mt-3 font-body text-sm text-[#D6E4F0]/80">
                    — said 400 times a day, always with the exact same soft-firm tone.
                  </p>
                </div>
              </div>
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
}
